import * as React from 'react'

import EmojiPicker, { EmojiClickData } from 'emoji-picker-react'
import {
  AtSign,
  Camera,
  Image,
  Paperclip,
  Plus,
  SendHorizontal,
  Smile,
  WorkflowIcon,
  X,
} from 'lucide-react'
import { action, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import { Button } from '~/client/src/shared/components/Button/Button'
import {
  FilePicker,
  IFilePickerActions,
} from '~/client/src/shared/components/FilePicker'
import * as Icons from '~/client/src/shared/components/Icons'
import OptionsItem from '~/client/src/shared/components/OptionsList/pieces/OptionsItem'
import OptionsListPanel from '~/client/src/shared/components/OptionsList/pieces/OptionsListPanel'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Delivery from '~/client/src/shared/models/Delivery'

import Message from '../../models/Message'
import Workflow from '../../models/Workflow'
import BaseAssignmentsStore from '../../stores/BaseAssignments.store'
import BaseFollowingsStore from '../../stores/BaseFollowings.store'
import {
  imageExtensions,
  msExcelExtensions,
  msPowerPointExtensions,
  msWordExtensions,
  pdfExtension,
} from '../../stores/domain/FileUploading.store'
import BaseActionBarStore from '../../stores/ui/BaseActionBar.store'
import ActionBarInput from '../ActionBarInput/ActionBarInput'
import ActionBarInputWithMentions from '../ActionBarInputWIthMentions/ActionBarInputWithMentions'
import ImagePreview from '../ImagePreview/ImagePreview'
import LinkedWorkflowPicker from '../LinkedWorkflowPicker/LinkedWorkflowPicker'
import { statusType } from './ActionBar'

type entityType = Message | Delivery | Workflow

interface IProps {
  entity: entityType
  actionBarStore: BaseActionBarStore
  isFocused: boolean
  formsRef: HTMLFormElement
  setIsFocused: (value: boolean) => void
  setFormsRef: (ref: HTMLDivElement) => void
  applyMessage?(): void
  isMobileMode?: boolean
  isSendButtonDisabled?: boolean

  followingsStore?: BaseFollowingsStore
  assignmentsStore?: BaseAssignmentsStore
  customStatusRenderer?: (status: statusType, stepId: string) => JSX.Element
  containerRef?: HTMLDivElement
}

@observer
export default class MessagesActionBar extends React.Component<IProps> {
  @observable private isLinkedWorkflowPickerOpen = false
  public constructor(props: IProps) {
    super(props)

    this.store.entityId = props.entity.id
    makeObservable(this)
  }

  private get store(): BaseActionBarStore {
    return this.props.actionBarStore
  }

  public componentDidUpdate(prevProps: Readonly<IProps>) {
    const { entity } = this.props

    if (entity !== prevProps.entity) {
      this.store.entityId = entity.id
      this.store.fullReset()
    }
  }

  public componentWillUnmount() {
    this.store.fullReset()
  }

  public render() {
    const {
      entity,
      assignmentsStore,
      followingsStore,
      containerRef,
      isFocused,
      setFormsRef,
    } = this.props

    const {
      draftMessage,
      decodedDraftMessage,
      mentionTags,
      setDraftMessage,
      isMentionActionActive,
      activateMentionAction,
      deactivateMentionAction,
      triggerMentionActionActivation,
      previewImage,
      file,
    } = this.store

    const shouldEnableMentions = !!followingsStore && !!assignmentsStore
    const className =
      'mention-input brada24 bg-white slack-bar-input no-resize full-width'
    const shouldShowSendButton = !!file || !!draftMessage || !!previewImage

    return (
      <>
        {this.linkedWorkflowPicker}
        <div
          ref={ref => setFormsRef(ref)}
          onFocus={this.onFocus}
          className={classList({
            'tw:min-h-0': true,
            'full-height': !isFocused,
            'scrollable tw:flex-1': isFocused,
          })}
        >
          <div
            className={classList({
              'tw:flex tw:gap-2.5 tw:py-2.5': true,
              'tw:items-start': !isFocused,
              'tw:flex-1 tw:flex-col': isFocused,
              'tw:w-[calc(100%-50px)]':
                (this.store.file || this.store.fileImage) && !isFocused,
            })}
          >
            {!isFocused && this.renderPlusButton()}
            {shouldEnableMentions ? (
              <div
                className={classList({
                  [className]: !!className,
                  'tw:p-2.5': true,
                  'tw:min-w-0 tw:flex-1': !isFocused,
                })}
              >
                <ActionBarInputWithMentions
                  onChange={setDraftMessage}
                  entityId={entity.id}
                  value={draftMessage}
                  plainText={decodedDraftMessage}
                  mentions={mentionTags}
                  className={className}
                  followingsStore={followingsStore}
                  assignmentsStore={assignmentsStore}
                  placeholder={Localization.translator.addANote}
                  onSuggestionsPopupHide={deactivateMentionAction}
                  onSuggestionsPopupShow={activateMentionAction}
                  parentRef={containerRef}
                />
                {isFocused && this.imagePreview}
                {isFocused && this.filePreview}
                {!isFocused && this.notFocusedFilesPreview}
              </div>
            ) : (
              <div className={className}>
                {!isFocused && this.notFocusedFilesPreview}
                {this.filePreview}
                <ActionBarInput
                  value={this.store.draftMessage}
                  onChange={this.setDraftMessage}
                  placeholder={Localization.translator.addANote}
                  className="slack-bar-input no-resize full-width"
                />
              </div>
            )}
          </div>
        </div>
        {isFocused && (
          <div className="tw:flex tw:shrink-0 tw:items-center tw:justify-between">
            <div className="tw:flex tw:items-center tw:gap-2.5">
              {this.renderPlusButton()}
              {shouldEnableMentions && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-lg"
                  aria-label={Localization.translator.mention}
                  className={classList({
                    'tw:rounded-full': true,
                    'tw:text-muted-foreground': !isMentionActionActive,
                    'tw:pointer-events-none tw:text-primary':
                      isMentionActionActive,
                  })}
                  onClick={triggerMentionActionActivation}
                >
                  <AtSign />
                </Button>
              )}
              {this.emojiMenu}
            </div>
            <Button
              type="button"
              size="icon-lg"
              aria-label={Localization.translator.send}
              className="tw:rounded-full"
              disabled={!shouldShowSendButton}
              onClick={this.send}
            >
              <SendHorizontal />
            </Button>
          </div>
        )}
      </>
    )
  }

  private cameraPicker = React.createRef<IFilePickerActions>()
  private libraryPicker = React.createRef<IFilePickerActions>()
  private attachmentPicker = React.createRef<IFilePickerActions>()

  @action.bound
  private uploadAttachment([file]: File[]) {
    this.store.uploadAttachment(file)
    this.closePlusMenu()
    this.props.setIsFocused(true)
    this.props.formsRef.focus()
  }

  @action.bound
  private addPhoto([file]: File[]) {
    this.store.setPreviewImage(file)
    this.closePlusMenu()
    this.props.setIsFocused(true)
    this.props.formsRef.focus()
  }

  @action.bound
  private openLinkedWorkflowPicker() {
    this.store.isPlusMenuShown = false
    // Focused, the composer is an absolute box — the picker sheet would anchor
    // to it instead of the panel.
    this.onBlur()
    this.isLinkedWorkflowPickerOpen = true
  }

  @action.bound
  private closeLinkedWorkflowPicker() {
    this.isLinkedWorkflowPickerOpen = false
  }

  @action.bound
  private setEmojiOpen(isOpen: boolean) {
    this.store.isEmojiOpened = isOpen
  }

  @action.bound
  private setPlusMenuOpen(isOpen: boolean) {
    this.store.isPlusMenuShown = isOpen
  }

  private promptForCamera = () => {
    this.promptForPhoto(this.cameraPicker)
  }

  private promptForLibrary = () => {
    this.promptForPhoto(this.libraryPicker)
  }

  private promptForPhoto(picker: React.RefObject<IFilePickerActions>) {
    this.store.resetPreviewImage()
    this.store.hideMenuPopup()
    picker.current?.open()
  }

  private promptForAttachment = () => {
    this.attachmentPicker.current?.open()
  }

  @action.bound
  private onFocus(event: React.FocusEvent) {
    // Only entering the note field expands the composer. The "+" button lives
    // in the same subtree and must survive its own click: expanding here would
    // re-render it into the toolbar before the click lands.
    if (event.target instanceof HTMLButtonElement) {
      return
    }
    this.props.setIsFocused(true)
  }

  @action.bound
  private onBlur() {
    this.props.setIsFocused(false)
  }

  @action.bound
  private onEmojiClick(event: EmojiClickData) {
    this.store.draftMessage += ` ${event.emoji}`
    this.store.isEmojiOpened = false
    this.props.setIsFocused(true)
    this.props.formsRef.focus()
  }

  private renderPlusButton() {
    const { isMobileMode } = this.props
    const title = Localization.translator.addToLog

    return (
      <ResponsiveSurface
        open={this.store.isPlusMenuShown}
        onOpenChange={this.setPlusMenuOpen}
        title={title}
        isMobileMode={isMobileMode}
        side="top"
        align="start"
        trigger={
          <Button
            type="button"
            variant="outline"
            size="icon-lg"
            aria-label={title}
            className="tw:shrink-0 tw:rounded-full"
          >
            <Plus />
          </Button>
        }
      >
        {this.renderAddToLogPanel(
          title,
          isMobileMode ? this.closePlusMenu : undefined,
        )}
      </ResponsiveSurface>
    )
  }

  // The sheet carries the header; anchored to the "+" button the context is
  // already obvious, so the popover renders the rows alone.
  private renderAddToLogPanel(
    title: string,
    onClose?: () => void,
  ): JSX.Element {
    return (
      <OptionsListPanel title={title} onClose={onClose}>
        {this.addToLogRows}
      </OptionsListPanel>
    )
  }

  @action.bound
  private closePlusMenu() {
    this.store.isPlusMenuShown = false
  }

  @action.bound
  private async send(evt: React.FormEvent<HTMLButtonElement>) {
    const { entity } = this.props
    evt.preventDefault()
    evt.stopPropagation()
    await this.store.replyToThread(entity)
    this.props.formsRef.blur()
    this.onBlur()
    this.props.applyMessage?.()
  }

  private setDraftMessage = (evt: React.FormEvent<HTMLInputElement>) => {
    const text = evt.currentTarget.value
    this.store.setDraftMessage(text, text, [])
  }

  public get imagePreview(): JSX.Element {
    if (!this.store.previewImage) {
      return
    }

    return (
      <ImagePreview
        removeImage={this.store.resetPreviewImage}
        className="preview-image-wrapper relative tw:mt-5 tw:max-w-16 tw:min-h-16 tw:overflow-visible"
        removeRightPosition={-7}
        removeTopPosition={-7}
      >
        <img src={this.store.previewImage} className="center" />
      </ImagePreview>
    )
  }

  private get notFocusedFilesPreview() {
    if (!this.store.file && !this.store.fileImage) {
      return
    }

    const filesAmount =
      (this.store.file ? 1 : 0) + (this.store.fileImage ? 1 : 0)
    return (
      <div
        className="tw:absolute tw:flex tw:shrink-0 tw:items-center"
        style={{ bottom: 20, right: 30 }}
      >
        <div className="tw:relative tw:flex tw:w-fit tw:items-center tw:gap-1 tw:rounded-lg tw:bg-muted tw:p-1.5">
          <Paperclip className="tw:size-4 tw:shrink-0 tw:text-muted-foreground" />
          <div className="tw:text-body-md tw:text-foreground">
            {filesAmount}
          </div>
        </div>
      </div>
    )
  }

  private get filePreview(): JSX.Element {
    if (!this.store.file) {
      return
    }

    return (
      <div className="tw:mt-5 tw:flex tw:shrink-0 tw:items-center">
        <div className="tw:relative tw:flex tw:max-w-[calc(100%-20px)] tw:items-center tw:rounded-sm tw:border tw:border-solid tw:border-border tw:bg-muted tw:p-1.5">
          <div
            onClick={this.store.resetFile}
            style={{ top: -10, right: -10, zIndex: 2 }}
            className="tw:absolute tw:z-[2] tw:flex tw:size-5 tw:cursor-pointer tw:items-center tw:justify-center tw:rounded-full tw:bg-foreground"
          >
            <X className="tw:size-3 tw:text-white" />
          </div>
          {this.fileIcon}
          <div className="tw:flex tw:min-w-0 tw:flex-col">
            <div className="tw:truncate tw:text-body-md tw:text-foreground">
              {this.store.file.name}
            </div>
            <div className="tw:text-body-md tw:text-muted-foreground">
              {this.fileTypeText}
            </div>
          </div>
        </div>
      </div>
    )
  }

  private get fileIcon(): JSX.Element {
    const className = 'tw:size-9 tw:shrink-0 tw:mr-2.5'
    const extension = this.getExtension(this.store.file?.name)
    switch (true) {
      case msWordExtensions.includes(extension):
        return <Icons.WordApp className={className} />
      case msPowerPointExtensions.includes(extension):
        return <Icons.PPTXApp className={className} />
      case msExcelExtensions.includes(extension):
        return <Icons.ExcelApp className={className} />
      case extension === pdfExtension:
        return <Icons.PDFApp className={className} />
      case imageExtensions.includes(extension):
        return <Image className={className} />
      default:
        return <Icons.File className={`${className} icon-dark-grey`} />
    }
  }

  private get fileTypeText(): string {
    const extension = this.getExtension(this.store.file?.name)
    switch (true) {
      case msWordExtensions.includes(extension):
        return 'MS Word'
      case msPowerPointExtensions.includes(extension):
        return 'MS PowerPoint'
      case msExcelExtensions.includes(extension):
        return 'MS Excel'
      case extension === pdfExtension:
        return 'PDF'
      case imageExtensions.includes(extension):
        return Localization.translator.image
      default:
        return null
    }
  }

  private get emojiMenu(): JSX.Element {
    const { isEmojiOpened } = this.store

    return (
      <ResponsiveSurface
        open={isEmojiOpened}
        onOpenChange={this.setEmojiOpen}
        title={Localization.translator.emojiPicker}
        isMobileMode={this.props.isMobileMode}
        sizing="fill"
        side="top"
        align="end"
        trigger={
          <Button
            type="button"
            variant="ghost"
            size="icon-lg"
            aria-label={Localization.translator.emojiPicker}
            className="tw:rounded-full tw:text-muted-foreground"
          >
            <Smile />
          </Button>
        }
      >
        <EmojiPicker
          height="100%"
          width="100%"
          open={true}
          className="tw:rounded-[inherit]! tw:border-0!"
          onEmojiClick={this.onEmojiClick}
        />
      </ResponsiveSurface>
    )
  }

  @action.bound
  private openMention() {
    this.store.isPlusMenuShown = false
    this.props.formsRef.focus()
    this.store.triggerMentionActionActivation()
  }

  private get addToLogRows(): JSX.Element {
    const { isMobileMode, entity } = this.props

    return (
      <>
        {/* The pickers own a hidden <input type=file>; the rows drive them so
            every row can be the same OptionsItem. */}
        {isMobileMode && (
          <FilePicker
            actionsRef={this.cameraPicker}
            accept="image/*"
            capture="environment"
            onPick={this.addPhoto}
          />
        )}
        <FilePicker
          actionsRef={this.libraryPicker}
          accept="image/*"
          onPick={this.addPhoto}
        />
        <FilePicker
          actionsRef={this.attachmentPicker}
          onPick={this.uploadAttachment}
        />

        {isMobileMode && (
          <OptionsItem
            icon={<Camera />}
            label={Localization.translator.camera}
            onClick={this.promptForCamera}
          />
        )}
        <OptionsItem
          icon={<Image />}
          label={Localization.translator.addPhotoFromLibrary}
          onClick={this.promptForLibrary}
        />
        <OptionsItem
          icon={<Paperclip />}
          label={Localization.translator.addFile}
          onClick={this.promptForAttachment}
        />
        <OptionsItem
          icon={<AtSign />}
          label={
            <span className="tw:flex tw:min-w-0 tw:flex-col tw:whitespace-normal">
              <span>{Localization.translator.atMentionAUser}</span>
              <span className="tw:text-app-micro tw:text-muted-foreground">
                {Localization.translator.atMentionUserDescription}
              </span>
            </span>
          }
          onClick={this.openMention}
        />
        {entity instanceof Workflow && (
          <OptionsItem
            icon={<WorkflowIcon />}
            label={Localization.translator.addLinkedWorkflow}
            onClick={this.openLinkedWorkflowPicker}
          />
        )}
      </>
    )
  }

  private get linkedWorkflowPicker(): JSX.Element {
    const { entity } = this.props
    if (!this.isLinkedWorkflowPickerOpen || !(entity instanceof Workflow)) {
      return null
    }

    return (
      <LinkedWorkflowPicker
        entity={entity}
        selectedWorkflowIds={entity.linkedWorkflows}
        title={Localization.translator.linkedWorkflows}
        onClose={this.closeLinkedWorkflowPicker}
      />
    )
  }

  private getExtension = (filename: string): string => {
    return filename?.split('.')?.pop()?.toLowerCase()
  }
}
